const randomClasses = [
  "loop-dog-image-1",
  "loop-dog-image-2",
  "loop-dog-image-3",
  "loop-dog-image-4",
  "loop-dog-image-5",
];

let randomClassesAvailable = [...randomClasses];

window.LoopUtils = {
  TIMEOUT_MS: 200,
  waitElement: async (selectorFunc, maxTries = 20) =>
    new Promise((resolve, reject) => {
      let tries = 0;
      try {
        function check() {
          const result = selectorFunc();
          if (typeof result === "undefined" || !result) {
            if (tries === maxTries) {
              reject();
              return;
            }
            tries++;
            setTimeout(check, window.LoopUtils.TIMEOUT_MS);
          } else {
            resolve(result);
            return;
          }
        }

        setTimeout(check, window.LoopUtils.TIMEOUT_MS);
      } catch (e) {}
    }),
  waitForElement: async (selector, callback, timeout = 2000) => {
    const start = Date.now();

    const check = () => {
      const elements =
        typeof selector === "string" ? document.querySelectorAll(selector) : [];
      if (elements.length)
        return callback(elements.length === 1 ? elements[0] : elements);
      requestAnimationFrame(check);
    };

    await new Promise(() => setTimeout(check, timeout));
  },

  getDogRandomClass: () => {
    if (randomClassesAvailable.length === 0) {
      randomClassesAvailable = [...randomClasses];
    }

    const randomIndex = Math.floor(
      Math.random() * randomClassesAvailable.length,
    );

    const randomClass = randomClassesAvailable[randomIndex];
    randomClassesAvailable.splice(randomIndex, 1);
    return randomClass;
  },

  /** Store country code, used as the dog's `country` on create. */
  getStoreCountry: () => window.Shopify?.country || "PT",

  /**
   * Normalises a dog form payload for the dogs API: snake_case keys, `weight`
   * as a string (the API types it as one), and empty values dropped so a
   * partial PATCH never blanks a field the user didn't touch.
   *
   * Unknown keys are stripped again by BarkynDogsApi.pickEditableFields — the
   * API rejects the whole request with 400 if any slip through.
   */
  buildDogApiPayload: (fields = {}) => {
    const payload = {};

    Object.entries(fields).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") return;
      payload[key] = key === "weight" ? String(value) : value;
    });

    return payload;
  },

  /** Escape a value before interpolating it into an innerHTML string. */
  escapeHtml: (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;"),

  /**
   * Dog photo URL, if the profile has one. `image_url` is the dogs API field;
   * `image_location` is checked as a fallback in case it holds a full URL
   * rather than a storage path.
   */
  DOG_PHOTO_KEYS: ["image_url", "image_location"],

  getDogPhotoUrl: (dog) => {
    if (!dog) return "";

    for (const key of window.LoopUtils.DOG_PHOTO_KEYS) {
      const value = dog[key];
      if (typeof value !== "string") continue;

      const url = value.trim();
      if (/^(https?:\/\/|data:image\/|\/\/|\/)/.test(url)) return url;
    }

    return "";
  },

  /**
   * The dogs DB stores gender/life stage as English slugs ("male", "puppy", …).
   * These helpers turn them into localized labels for display.
   */
  getDogGenderLabel: (dog) => {
    const gender = String(dog?.gender || "").toLowerCase();
    if (gender === "male" || gender === "m") return window.t("dogs.male");
    if (gender === "female" || gender === "f") return window.t("dogs.female");
    return dog?.gender || "";
  },

  getDogLifeStageLabel: (dog) => {
    const lifeStage = String(dog?.life_stage || dog?.lifeStage || "")
      .toLowerCase()
      .replace(/[\s_-]/g, "");
    if (lifeStage === "puppy") return window.t("dogs.puppy");
    if (lifeStage === "adult") return window.t("dogs.adult");
    if (lifeStage === "senior") return window.t("dogs.senior");
    return dog?.life_stage || dog?.lifeStage || "";
  },

  /**
   * Gendered article that precedes a dog name ("o Rocky" / "a Luna" in PT).
   * Locales that don't use one have it translated as an empty string.
   */
  getDogArticle: (dog) => {
    const gender = String(dog?.gender || "").toLowerCase();
    const key =
      gender === "female" || gender === "f"
        ? "dogs.articleFemale"
        : "dogs.articleMale";
    return window.t(key) || "";
  },

  /**
   * "Para o Rocky", "Para o Rocky e a Luna", "Para o Rocky, a Luna e o Bobi".
   * Returns an empty string when there is no dog to name.
   */
  formatDogsForCopy: (dogs = []) => {
    const named = (dogs || []).filter((dog) => dog && dog.name);
    if (named.length === 0) return "";

    const withArticle = named.map((dog) => {
      const article = window.LoopUtils.getDogArticle(dog);
      return article ? `${article} ${dog.name}` : dog.name;
    });

    const separator = window.t("dogs.nameSeparator");
    const names =
      withArticle.length === 1
        ? withArticle[0]
        : `${withArticle.slice(0, -1).join(", ")} ${separator} ${
            withArticle[withArticle.length - 1]
          }`;

    return window.t("dogs.forDogs", names);
  },

  createElementWithClasses: (tag, classNames = []) => {
    const el = document.createElement(tag);
    el.classList.add(...classNames);
    return el;
  },

  addClasses: (selector, classes) => {
    document
      .querySelectorAll(selector)
      .forEach((el) => el.classList.add(...classes));
  },

  removeClasses: (selector, classes) => {
    document
      .querySelectorAll(selector)
      .forEach((el) => el.classList.remove(...classes));
  },

  getCustomerId: async () => {
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds total

    while (attempts < maxAttempts) {
      if (window.Shopify?.customerId) {
        return window.Shopify.customerId;
      }

      // Wait 100ms before trying again
      await new Promise((resolve) => setTimeout(resolve, 100));
      attempts++;
    }

    console.error("Customer ID not found after waiting 5 seconds");
    return null;
  },

  waitLoopProps: async (options) => {
    const { max_attempts, required_keys } = {
      max_attempts: 20,
      required_keys: ["allSubscriptions", "customer", "subscription"],
      ...options,
    };
    let current_attempt = 0;
    return new Promise((resolve, reject) => {
      function checkLoopProps() {
        if (current_attempt++ >= max_attempts) {
          reject();
          return;
        }
        if (window.loopProps) {
          let keyMissing = false;
          if (required_keys.length > 0) {
            for (const key of required_keys) {
              if (!Object.keys(window.loopProps).includes(key)) {
                keyMissing = true;
                break;
              }
            }
          }
          if (!keyMissing) {
            resolve(window.loopProps);
            return;
          }
        }
        setTimeout(checkLoopProps, window.LoopUtils.TIMEOUT_MS);
      }
      checkLoopProps();
    });
  },
};
